/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';

import { MILLISECONDS_IN_A_SECOND } from '../../../config';
import { AppUtils } from '../../AppUtils';
import { Constants } from '../../constants';
import { DeviceModel } from '../../device';
import { LiveTvError } from '../../error';
import { Log } from '../../logging';
import { Storage } from '../../storage/Storage';
import { ITivoSubscription, TivoSubscriptionModel } from '../../tivo';
import { DateTime } from '../../utils';
import { VideoPlayerStates } from '../enums';
import { LiveTvPlaybackErrorMapper } from '../error';
import {
    IPlayerStats,
    IPlayerStatus,
    ISeekRange,
    ITextTrack,
    IVariantTrack,
    IVideoPlayer,
    IVideoVariant,
} from '../interfaces';
import { ShakaPlayerUtils } from '../utils';
import shakaConfig from './ShakaPlayerConfig';

// TODO: To be used in release build. [KST-2698]
const shaka = require('shaka-player/shaka-player.compiled');
// const shaka = require('shaka-player/shaka-player.compiled.debug');
shaka.polyfill.installAll();

export class ShakaVideoPlayerImpl implements IVideoPlayer {
    private static TAG = 'ShakaVideoPlayerImpl';

    private static RETRY_LIMIT = 5;

    private mAudioTracks: IVariantTrack[] = [];

    private mErrorSubscription = new TivoSubscriptionModel<LiveTvError>();

    private mErrorTime: any;

    private mIsClosedCaptionsEnabled = false;

    // Currently, Shaka Player does not provide a listener to detect the first frame event.
    // Therefore, this flag is necessary to indicate when the first frame event should be logged.
    private mIsFirstFrameTriggered: boolean = false;

    private isPreviousDynamic: boolean = false;

    private mMediaElement: HTMLMediaElement | null = null;

    private mPlaybackErrorIntervalId: any = null;

    private mPlaybackErrorRetryCount: number;

    private mPlayerPositionBeforeError: number = 0;

    private mPlaybackRetryTimerId: NodeJS.Timeout | undefined;

    private mPlayerStateSubscription = new TivoSubscriptionModel<IPlayerStatus>();

    private mPlayerStatus: IPlayerStatus;

    private mSeekRangeStartTime: number = 0;

    private mTextTracks: ITextTrack[] = [];

    private mTuningUri: string = '';

    private mVideoPlayer: any;

    public constructor() {
        this.mErrorTime = null;
        this.mPlaybackErrorRetryCount = 0;
        this.mPlaybackRetryTimerId = undefined;
        this.mPlayerStatus = { state: VideoPlayerStates.NONE };
        this.mVideoPlayer = new shaka.Player();
        // TODO: To be used in release build. [KST-2698]
        // shaka.log.setLevel(shaka.log.Level.V2);
        this.setUpPlayer();
    }

    private clearPlayerErrorTimer(): void {
        if (this.mPlaybackErrorIntervalId) {
            clearInterval(this.mPlaybackErrorIntervalId);
            this.mPlaybackErrorIntervalId = null;
        }
    }

    private getAudioLanguages(): string[] {
        return this.mVideoPlayer.getAudioLanguages();
    }

    private getDefaultTextTrack(): any {
        const defaultTextTrack = this.mTextTracks.find(({ primary }) => primary === true);
        return defaultTextTrack || this.mTextTracks[0];
    }

    private getPlayerEvents() {
        return [
            { name: 'adaptation', handler: this.onAdaptationHandler },
            { name: 'buffering', handler: this.onBufferingHandler },
            { name: 'emsg', handler: this.onEmsgHandler },
            { name: 'error', handler: this.onErrorHandler },
            { name: 'manifestparsed', handler: this.onManifestParsedHandler },
            { name: 'metadata', handler: this.onMetaDataHandler },
            { name: 'onstateidle', handler: this.onStateIdleHandler },
            { name: 'ratechange', handler: this.onRateChangeHandler },
            { name: 'streaming', handler: this.onStreamingHandler },
            { name: 'textchanged', handler: this.onTextChangedHandler },
            { name: 'trackschanged', handler: this.onTracksChangedHandler },
            { name: 'unloading', handler: this.onUnloadingHandler },
        ];
    }

    private handleCaptionPreference(): void {
        if (this.mTextTracks.length > 0) {
            this.setTextTrackVisibility(this.mIsClosedCaptionsEnabled);
            const selectedLanguage = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItemWithDefaultValue(Storage.Keys.PLAYER_CC_LANGUAGE, '');
            const selectedKind = Storage.getInstance(
                Storage.StorageType.SESSION_STORAGE,
            ).getItemWithDefaultValue(Storage.Keys.PLAYER_CC_LANGUAGE_KIND, '');
            if (this.mIsClosedCaptionsEnabled)
                this.setTextTrackByLanguage(selectedLanguage, selectedKind);
        }
    }

    private async handlePlaybackError(error: any) {
        const err = new LiveTvPlaybackErrorMapper().getErrorByErrorObject(error);
        const errorCode = err.getCode();

        const uiErrorCode = errorCode.split('-')[0];
        const subErrorCode = errorCode.split('-')[1];

        // in case of network error, we check if video buffer is availabe the we play buffer
        // and once buffer is empty then we show error
        if (
            // KST-5305: add a check for trickplay
            ShakaPlayerUtils.isShakaNetworkError(error) &&
            this.isErrorBufferEnded(err, uiErrorCode, subErrorCode)
        ) {
            this.handleShakaNetworkError(err, uiErrorCode, subErrorCode);
        } else if (
            ShakaPlayerUtils.isRetryableError(error) &&
            this.mPlaybackErrorRetryCount < ShakaVideoPlayerImpl.RETRY_LIMIT
        ) {
            this.mPlayerStateSubscription.notify({
                state: VideoPlayerStates.ERROR_RECOVERY,
                code: errorCode,
            });

            if (this.mMediaElement?.currentTime) {
                this.mPlayerPositionBeforeError = this.mMediaElement?.currentTime;
            }
            await this.stop();
            // setting streamingRecoveryFlag true as player entered recovery
            this.mPlaybackRetryTimerId = setTimeout(() => {
                this.mPlaybackErrorRetryCount += 1;
                if (this.mTuningUri) {
                    this.play(this.mPlayerPositionBeforeError);
                }
                this.log('debug', 'retryCount: ', this.mPlaybackErrorRetryCount);
            }, Constants.PLAYER_RETRY_TIMEOUT);
        } else if (!ShakaPlayerUtils.isShakaNetworkError(error)) {
            // setting false in case the error did not recover
            this.mErrorSubscription.notify(err);
        }
    }

    private async handleShakaNetworkError(
        error: LiveTvError,
        uiErrorCode: string,
        subErrorCode: string,
    ) {
        if (this.mPlayerStatus.state === VideoPlayerStates.PAUSE) return;
        if (this.mPlaybackErrorRetryCount < ShakaVideoPlayerImpl.RETRY_LIMIT) {
            this.mErrorTime = null;
            this.mPlayerStateSubscription.notify({
                state: VideoPlayerStates.ERROR_RECOVERY,
                code: `${uiErrorCode}-${subErrorCode}`,
            });
            if (this.mMediaElement?.currentTime) {
                this.mPlayerPositionBeforeError = this.mMediaElement?.currentTime;
            }
            await this.stop();
            this.mPlaybackRetryTimerId = setTimeout(() => {
                this.mPlaybackErrorRetryCount += 1;
                if (this.mTuningUri && this.mVideoPlayer)
                    this.play(this.mPlayerPositionBeforeError);
            }, Constants.PLAYER_RETRY_TIMEOUT);
        } else {
            // setting false in case the error did not recover
            this.mErrorSubscription.notify(error);
        }
    }

    private isPlaybackStarted(): boolean {
        return !!this.mVideoPlayer && !!this.getPlayerStats()?.playTime;
    }

    private isEndOfStream(): boolean {
        const isDynamicToStatic = this.isDynamicToStatic();
        if (this.mMediaElement?.ended) return true;
        const isProgramEnded = this.getPlayerCurrentTime() >= this.getDuration();
        if (this.mVideoPlayer.isInProgress() || isProgramEnded) {
            return isProgramEnded;
        }
        // This old logic where mMediaElement properties ended and duration was not coming wrong.
        if (isDynamicToStatic) {
            const playerCurrentTime = this.getPlayerCurrentTime();
            const lastViewedPosition = Math.round(playerCurrentTime - this.mSeekRangeStartTime);
            if (lastViewedPosition && lastViewedPosition >= 0) this.play(lastViewedPosition);
            return false;
        }
        if (!this.isDynamicManifest() && this.getSeekRange().end > 0) {
            const endTime = this.getSeekRange().end;
            if (endTime - this.getPlayerCurrentTime() < Constants.END_OF_THRESHOLD) return true;
        }
        return false;
    }

    private isDynamicToStatic(): boolean {
        if (this.isPreviousDynamic && !this.isDynamicManifest()) {
            this.isPreviousDynamic = false;
            return true;
        }
        return false;
    }

    /**
     * check if video play cache is played
     * @returns true if video buffer cache is played
     */
    private isErrorBufferEnded(
        error: LiveTvError,
        uiErrorCode: string,
        subErrorCode: string,
    ): boolean {
        const currentTime = Date.now();

        // setting streamingRecoveryFlag true as player entered recovery
        let isBufferEnded = true;
        if (
            (this.isPlaybackStarted() || this.mPlaybackErrorRetryCount > 0) &&
            this.mErrorTime === null
        ) {
            this.mErrorTime = new DateTime(new Date()).addSeconds(
                this.mPlaybackErrorRetryCount > 0
                    ? Constants.THIRTY_SECONDS
                    : Constants.FIFTY_SECONDS,
            );
            isBufferEnded = false;
        } else if (this.mErrorTime !== null && this.mErrorTime.getTime() > currentTime) {
            isBufferEnded = false;
        } else if (!this.isPlaybackStarted() && this.mPlaybackErrorRetryCount === 0) {
            this.mPlaybackErrorRetryCount = ShakaVideoPlayerImpl.RETRY_LIMIT;
            isBufferEnded = true;
        }
        this.clearPlayerErrorTimer();
        this.mPlaybackErrorIntervalId = setInterval(
            () => {
                this.handleShakaNetworkError(error, uiErrorCode, subErrorCode);
            },
            this.mPlaybackErrorRetryCount > 0
                ? Constants.THIRTY_SECONDS_IN_MILLISECOND
                : Constants.FIFTY_SECONDS_IN_MILLISECOND,
        );
        return isBufferEnded;
    }

    // eslint-disable-next-line class-methods-use-this
    private log(
        level: 'debug' | 'info' | 'error',
        message: string,
        ...optionalParams: any[]
    ): void {
        Diagnostics[level](ShakaVideoPlayerImpl.TAG, message, ...optionalParams);
        Log[level[0]](ShakaVideoPlayerImpl.TAG, message, ...optionalParams);
        if (level === 'error')
            Diagnostics.error(ShakaVideoPlayerImpl.TAG, 'stacktrace:,', optionalParams[0]);
    }

    private onAdaptationHandler = (event) => {
        this.setVideoPlayerState({
            state: VideoPlayerStates.ADAPTATION,
            event: {
                oldTrack: event?.Af,
            },
        });
    };

    private onBufferingHandler = (state: any) => {
        if (state.buffering === true) {
            this.setVideoPlayerState({ state: VideoPlayerStates.BUFFERING });
        }
    };

    private onEmsgHandler = () => {
        this.log('info', '{ShakaPlayerEvent: Emsg}');
    };

    private onErrorHandler = (error: any) => {
        this.log('error', '{ShakaPlayerEvent: Emsg}', error.detail);
        if (ShakaPlayerUtils.isErrorHandlingRequired(error.detail)) {
            this.handlePlaybackError(error.detail);
        }
    };

    private onManifestParsedHandler = (event) => {
        this.log('info', '{ShakaPlayerEvent: Manifest Parsed}');
        this.setVideoPlayerState({
            state: VideoPlayerStates.MANIFEST_PARSED,
            event: {
                eventTimestamp: event.timeStamp,
            },
        });
    };

    private onMetaDataHandler = () => {
        this.log('info', '{ShakaPlayerEvent: Metadata Found}');
    };

    private onPauseHandler = (event) => {
        this.log('info', '{ShakaPlayerEvent: Pause}');
        this.setVideoPlayerState({
            state: VideoPlayerStates.PAUSE,
            event: {
                eventTimestamp: event.timeStamp,
            },
        });
    };

    private onPlayingHandler = (event) => {
        this.setVideoPlayerState({
            state: VideoPlayerStates.PLAYING,
            event: {
                eventTimestamp: event.timeStamp,
            },
        });
        if (this.mPlaybackRetryTimerId) {
            clearTimeout(this.mPlaybackRetryTimerId);
            this.mPlaybackRetryTimerId = undefined;
        }
        this.clearPlayerErrorTimer();
        this.mPlayerPositionBeforeError = 0;
        if (!this.mIsFirstFrameTriggered) {
            this.mIsFirstFrameTriggered = true;
            this.mSeekRangeStartTime = this.getSeekRange().start;
            this.setVideoPlayerState({ state: VideoPlayerStates.FIRST_FRAME });
            this.log('info', 'FirstFrameTriggered');
        }
    };

    private onRateChangeHandler = (state: any) => {
        if (!state.buffering) {
            this.setVideoPlayerState({ state: VideoPlayerStates.RATE_CHANGE });
        }
    };

    private onStalledHandler = (event) => {
        this.log('info', '{ShakaPlayerEvent: Stalled}');
        this.setVideoPlayerState({
            state: VideoPlayerStates.STALLED,
            event: {
                eventTimestamp: event.timeStamp,
            },
        });
    };

    private onStateIdleHandler = () => {
        this.log('info', '{ShakaPlayerEvent: State Idle}');
    };

    private onStreamingHandler = () => {
        this.log('info', '{ShakaPlayerEvent: Streaming}');
    };

    private onTextChangedHandler = () => {
        this.log('info', '{ShakaPlayerEvent: Text Tracks Changed}');
        this.getTextTracks();
    };

    private onTimeUpdateHandler = () => {
        if (this.isEndOfStream()) {
            this.log('info', '{ShakaPlayerEvent: Time Update - End of Stream}');
            this.setVideoPlayerState({ state: VideoPlayerStates.END_OF_STREAM });
        }
    };

    private onTracksChangedHandler = () => {
        this.log('info', '{ShakaPlayerEvent: Tracks Changed}');
    };

    private onUnloadingHandler = () => {
        this.log('info', '{ShakaPlayerEvent: Unloading}');
        this.setVideoPlayerState({ state: VideoPlayerStates.UNLOADED });
    };

    private onVideoPlayerStateChangeHandler(): void {
        this.getPlayerStateChangeListener().notify(this.mPlayerStatus);
    }

    private registerEventListeners = () => {
        if (this.mMediaElement) {
            this.mMediaElement!.addEventListener('pause', this.onPauseHandler);
            this.mMediaElement!.addEventListener('playing', this.onPlayingHandler);
            this.mMediaElement!.addEventListener('stalled', this.onStalledHandler);
            this.mMediaElement!.addEventListener('timeupdate', this.onTimeUpdateHandler);
        }
        this.getPlayerEvents().forEach((event) =>
            this.mVideoPlayer.addEventListener(event.name, event.handler),
        );
    };

    private setUpPlayer(): void {
        if (this.mVideoPlayer && shakaConfig) {
            this.mVideoPlayer.configure(shakaConfig);
        }
    }

    private setVideoPlayerState(status: IPlayerStatus): void {
        this.mPlayerStatus = status;
        this.onVideoPlayerStateChangeHandler();
    }

    public initPlayer(videoEl: HTMLVideoElement, uiContainerRef: HTMLDivElement): void {
        this.log('debug', 'Configure');
        if (!this.mVideoPlayer) {
            this.log('error', 'Player object initilisation failed');
        }
        this.mMediaElement = videoEl;
        this.mVideoPlayer.attach(videoEl);
        this.mVideoPlayer.setVideoContainer(uiContainerRef);
        this.registerEventListeners();
        this.mIsClosedCaptionsEnabled = Storage.getInstance(
            Storage.StorageType.SESSION_STORAGE,
        ).getItemWithDefaultValue(Storage.Keys.PLAYER_CC_STATUS, false);
    }

    public isDynamicManifest(): boolean {
        return this.mVideoPlayer.isLive();
    }

    public getAudioTracks(): IVariantTrack[] {
        if (this.mAudioTracks.length === 0) {
            const variants = this.getVariantTracks();
            const audioLanguages = this.getAudioLanguages();
            const uniqueAudioTracks: Map<number, IVariantTrack> = new Map();

            audioLanguages.forEach((lang: string) => {
                variants.forEach((variant: IVariantTrack) => {
                    if (
                        variant.audioId !== null &&
                        !uniqueAudioTracks.has(variant.audioId) &&
                        variant.language === lang
                    ) {
                        uniqueAudioTracks.set(variant.audioId, variant);
                    }
                });
            });

            this.mAudioTracks = Array.from(uniqueAudioTracks.values());
        }

        this.log('info', 'Found Supported Tracks {mAudioTracks:', this.mAudioTracks);
        return this.mAudioTracks;
    }

    public getDuration(): number {
        return this.mMediaElement?.duration ?? Infinity;
    }

    public getPlayerCurrentTime(): number {
        return this.mMediaElement?.currentTime ?? Date.now() / MILLISECONDS_IN_A_SECOND;
    }

    public getPlayerErrorListener(): ITivoSubscription<LiveTvError> {
        return this.mErrorSubscription;
    }

    public getPlayerStateChangeListener(): ITivoSubscription<IPlayerStatus> {
        return this.mPlayerStateSubscription;
    }

    public getPlayerStats(): IPlayerStats {
        return this.mVideoPlayer?.getStats() || {};
    }

    public getSeekRange(): ISeekRange {
        return this.mVideoPlayer.seekRange();
    }

    public getTextTracks(): ITextTrack[] {
        this.mTextTracks = this.mVideoPlayer.getTextTracks();
        this.log('debug', 'getTextTracks mTextTracks:', this.mTextTracks);
        return this.mTextTracks;
    }

    public getVariantTracks(): IVariantTrack[] {
        return this.mVideoPlayer.getVariantTracks();
    }

    public getVideoVarients(): IVideoVariant[] {
        return this.mVideoPlayer.getVideoVarients();
    }

    public isTextTrackChangeSupported(): boolean {
        return this.mTextTracks.length > 1;
    }

    public isTextTrackSupported(): boolean {
        return this.mTextTracks.length > 0;
    }

    public mute(muteAudio: boolean): void {
        if (this.mMediaElement) this.mMediaElement.muted = muteAudio;
    }

    public overrideAvailabilityWindowForLinear(): void {
        let availabilityWindow =
            this.mVideoPlayer.seekRange().end - this.mVideoPlayer.seekRange().start;
        const availabilityWindowFromConfig =
            this.mVideoPlayer.getConfiguration().manifest.availabilityWindowOverride;
        if (availabilityWindowFromConfig) {
            availabilityWindow = Math.min(availabilityWindow, availabilityWindowFromConfig);
        }
        this.mVideoPlayer.configure({
            manifest: {
                availabilityWindowOverride: availabilityWindow,
            },
        });
    }

    public overrideAvailabilityWindowForStartOver(): void {
        this.mVideoPlayer.configure({
            manifest: {
                availabilityWindowOverride: Infinity,
            },
        });
    }

    public pause(isPaused: boolean): void {
        if (this.mMediaElement)
            if (isPaused) {
                this.mMediaElement.pause();
            } else {
                this.mMediaElement.play();
            }
    }

    public async play(position?: number): Promise<void> {
        this.clearPlayerErrorTimer();
        const playerMsoConfig = DeviceModel.getTiVoSettings().getPlayerMsoConfig();
        this.mVideoPlayer.configure(playerMsoConfig);
        if (this.mPlayerStatus.state === VideoPlayerStates.PLAYING) {
            this.log('debug', 'Stopping playback as alredy playing');
            await this.stop();
        }
        if (this.mTuningUri)
            this.mVideoPlayer
                .load(this.mTuningUri)
                .then(() => {
                    this.setVideoPlayerState({
                        state: VideoPlayerStates.READY,
                    });
                    this.isPreviousDynamic = this.isDynamicManifest();
                    this.log('debug', 'The video has now been loaded!');

                    this.getTextTracks();
                    this.handleCaptionPreference();
                    this.setVideoPlayerState({
                        state: VideoPlayerStates.LOADED,
                    });

                    this.mIsFirstFrameTriggered = false;
                    if (position) {
                        this.setPlayPosition(position);
                    }
                })
                .catch((error) => {
                    if (ShakaPlayerUtils.isErrorHandlingRequired(error)) {
                        this.handlePlaybackError(error);
                    }
                });
    }

    /**
     * This function is not being used now as player is never detached in the lifecycle of app.
     * We will need this when we will detach the player.
     */
    public removeEventListners = () => {
        if (this.mMediaElement) {
            this.mMediaElement!.removeEventListener('pause', this.onPauseHandler);
            this.mMediaElement!.removeEventListener('playing', this.onPlayingHandler);
            this.mMediaElement!.removeEventListener('stalled', this.onStalledHandler);
            this.mMediaElement!.removeEventListener('timeupdate', this.onTimeUpdateHandler);
        }
        this.getPlayerEvents().forEach((event) =>
            this.mVideoPlayer.removeEventListener(event.name, event.handler),
        );
    };

    public resetPlayerConfiguration(): void {
        this.log('info', 'resetPlayerConfiguration');
        this.mVideoPlayer.resetConfiguration();
        this.setUpPlayer();
    }

    public selectAudioTrackByLanguage(language: string): void {
        this.log('info', `setAudioTrackByLanguage {language: `, language, '}');
        this.mVideoPlayer.selectAudioLanguage(language);
    }

    public selectTextTrack(track: ITextTrack): void {
        this.log('info', `selectTextTrack {track: `, track, '}');
        this.mVideoPlayer.selectTextTrack(track);
    }

    public setAudioTrack(track: IVariantTrack) {
        this.mVideoPlayer.selectVariantTrack(track, true /* clearBuffer */);
    }

    public setTextTrackByLanguage(sLanguage?: string, sKind?: string): number {
        if (this.mTextTracks.length === 0) {
            this.log('debug', 'No supported Subtitle available');
            return -1;
        }
        this.setTextTrackVisibility(true);
        const selectedTrack = this.mTextTracks.find(
            ({ language, kind }) =>
                (language === sLanguage && sKind === kind) || language === sLanguage,
        );
        const track = selectedTrack || this.getDefaultTextTrack();
        if (track) {
            this.selectTextTrack(track);
            return track.id;
        }
        return -1;
    }

    public setUpDRM(url: string, header: string, vuid: string, fullUrl?: string): void {
        if (DeviceModel.inFrame()) {
            this.mVideoPlayer.configure({
                drm: {
                    servers: {
                        'com.widevine.alpha':
                            fullUrl || `${url}?${header}=${AppUtils.getBase64String(vuid)}`,
                    },
                },
            });
        } else {
            this.mVideoPlayer.configure({
                drm: {
                    servers: {
                        'com.widevine.alpha':
                            fullUrl || `${url}?${header}=${AppUtils.getBase64String(vuid)}`,
                    },
                    advanced: {
                        'com.widevine.alpha': {
                            persistentStateRequired: true,
                        },
                    },
                },
            });
        }
        this.setVideoPlayerState({ state: VideoPlayerStates.DRM_CONFIGURED });
    }

    public setPlayPosition(position: number): void {
        if (this.mMediaElement && this.mVideoPlayer) {
            const newTime = this.mMediaElement.currentTime + position;
            const { start: seekRangeStart, end: seekRangeEnd }: ISeekRange =
                this.mVideoPlayer.seekRange();
            if (newTime >= seekRangeStart && newTime <= seekRangeEnd) {
                this.mMediaElement.currentTime = newTime;
            } else {
                this.mMediaElement.currentTime = position > 0 ? seekRangeEnd : seekRangeStart;
            }
            this.mPlayerPositionBeforeError = 0;
            this.log('info', 'setPlayPosition: ', this.mMediaElement.currentTime);
        }
    }

    public setPlayUri(tuningUri: string): void {
        this.log('info', `setPlayUri {tuningUri: `, tuningUri, '}');
        this.setVideoPlayerState({ state: VideoPlayerStates.INITIATION });
        this.mTuningUri = tuningUri;
        this.setVideoPlayerState({ state: VideoPlayerStates.LOADING });
        this.mPlaybackErrorRetryCount = 0;
        this.play();
    }

    public setTextTrackVisibility(isVisible: boolean): void {
        this.log('info', `setTextTrackVisibility {isVisible: `, isVisible, '}');

        if (this.mIsClosedCaptionsEnabled !== isVisible) {
            Storage.getInstance(Storage.StorageType.SESSION_STORAGE).setItem(
                Storage.Keys.PLAYER_CC_STATUS,
                isVisible,
            );
            this.mIsClosedCaptionsEnabled = isVisible;
        }
        this.mVideoPlayer.setTextTrackVisibility(isVisible);
    }

    public stop(): Promise<void> {
        this.log('debug', ' stop');
        return this.mVideoPlayer
            .unload()
            .then(() => {
                this.log('info', `PlayerEvent Player unloaded successfully.`);
                this.setVideoPlayerState({ state: VideoPlayerStates.UNLOADED });
            })
            .catch((error) => {
                this.log('error', 'PlayerEvent Error unloading player:', error);
            })
            .finally(() => {
                // Cleanup
                this.mAudioTracks = [];
                this.mIsFirstFrameTriggered = false;
                this.mPlayerStatus = { state: VideoPlayerStates.NONE };
                this.mTextTracks = [];
                clearTimeout(this.mPlaybackRetryTimerId);
                this.mPlaybackRetryTimerId = undefined;
                this.clearPlayerErrorTimer();
            });
    }
}
